import React, { useCallback } from 'react';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as cts from '../../../constants/ui/svg';
import { Switch, Input, Select, Button } from 'antd';
import img from '../../../assets/images/img.png';
import { useAppSelector, useAppDispatch } from 'hooks';
import { getToken } from 'helpers';
import { rankingAction } from 'slices/rankingSlice';
import { errorToast, successToast } from 'helpers';
import { NoteAppRankingTypes, NoteStyleConfig, PermissionStaffCreatorModel } from 'models';
import ModalTextEditor from './ModalTextEditor';

type Props = {};

type NoteAppRankingLocal = NoteAppRankingTypes & {
  is_edit?: number;
  delete?: number;
};

type rankArr = {
  id?: number;
  title: string;
  value: number;
  note?: string;
  rank?: number;
};

const Memo = forwardRef((props: any, ref) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const token = getToken() as string;

  const noteAppRanking = useAppSelector((state) => state.ranking.noteAppRanking);
  const hostForOrderList = useAppSelector((state) => state.ranking.hostForOrderList);
  const selectShop = useAppSelector((state) => state.shopList.selectShop);
  const noteStaffAppRanking = useAppSelector((state) => state.ranking.noteStaffAppRanking);
  const masterInfo = useAppSelector((state) => state.auth.masterInfo);
  const listPermissionByUser = useAppSelector((state) => state.shopSetting.permissionByUser);
  const isMaster = masterInfo.active && !selectShop.id;

  const [selectRank, setSelectRank] = useState(isMaster ? 'ranking' : 'staff');
  const [notes, setNotes] = useState<{
    staff: NoteAppRankingLocal[];
    ranking: NoteAppRankingLocal[];
  }>({ staff: [], ranking: [] });

  const [showModal, setShowModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<NoteAppRankingLocal | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const listShopHasPermission = listPermissionByUser
    .filter((item) => item.name == 'enable_ranking_program_master')
    .map((mapItem) => mapItem.shop_id);
  const rankArr: any = [
    { title: 'No.1', value: 1 },
    { title: 'No.2', value: 2 },
    { title: 'No.3', value: 3 },
    { title: 'No.4', value: 4 },
    { title: 'No.5', value: 5 },
    { title: 'No.6', value: 6 },
    { title: 'No.7', value: 7 },
    { title: 'No.8', value: 8 },
    { title: 'No.9', value: 9 },
    { title: 'No.10', value: 10 },
  ];
  const staffArr = [...(hostForOrderList.data || [])]
    .sort((x: any, y: any) => (y?.orderby_staff || 0) - (x?.orderby_staff || 0))
    .map((x: any) => {
      return { user_id: x?.cid_user };
    });

  // Get style config from NoteAppRankingTypes object
  const getStyleConfig = useCallback(
    (noteItem?: Partial<NoteAppRankingTypes> | null): NoteStyleConfig => {
      if (!noteItem) {
        return {
          font: 'NotoSansNormal',
          font_size: 24,
          color: '#E9EAEB',
          stroke_color: '#252B37',
          stroke_width: 10,
        };
      }

      return {
        font: noteItem.font || 'NotoSansNormal',
        font_size: noteItem.font_size || 24,
        color: noteItem.color || '#E9EAEB',
        stroke_color: noteItem.stroke_color || '#252B37',
        stroke_width: noteItem.stroke_width || 10,
      };
    },
    []
  );

  // Get rank title
  const getRankTitle = useCallback((rank: number): string => {
    return `No.${rank}`;
  }, []);

  const handleCheckMasterCreateNote = (noteItem: any) => {
    const permissionStaffArr = noteItem.permissions_staff_creator;
    if (permissionStaffArr?.length !== 0) {
      const findItemHasMasterPermission = permissionStaffArr?.findIndex(
        (item: PermissionStaffCreatorModel) => item.name == 'enable_ranking_program_master'
      );
      if (findItemHasMasterPermission !== -1 && !noteItem.shop_id) {
        return true;
      }
    }
    return false;
  };
  useEffect(() => {
    // merge helper preserves local is_edit rows
    const mergeByKey = (
      current: NoteAppRankingLocal[],
      incoming: NoteAppRankingLocal[],
      pickKey: (i: NoteAppRankingLocal) => string | number
    ): NoteAppRankingLocal[] => {
      const map = new Map<string | number, NoteAppRankingLocal>();
      current.forEach((c) => map.set(pickKey(c), c));
      for (const inc of incoming) {
        const key = pickKey(inc);
        const existed = map.get(key);
        if (existed && existed.is_edit) {
          // keep local edits
          map.set(key, { ...existed });
        } else {
          map.set(key, inc);
        }
      }
      return Array.from(map.values());
    };

    if (selectRank === 'staff') {
      setNotes((prev) => {
        const updatedStaffRanking = staffArr.map(({ user_id }: any) => {
          const matchedNote = noteStaffAppRanking.find((item) => item.user_id === user_id);

          // Check if there's a local edit for this user_id
          const localEdit = prev.staff?.find(
            (s: NoteAppRankingLocal) => s.user_id === user_id && s.is_edit
          );

          return {
            ...matchedNote,
            id: matchedNote?.id || null,
            note: localEdit?.note ?? matchedNote?.note ?? '',
            font: localEdit?.font ?? matchedNote?.font ?? null,
            font_size: localEdit?.font_size ?? matchedNote?.font_size ?? null,
            color: localEdit?.color ?? matchedNote?.color ?? null,
            stroke_color: localEdit?.stroke_color ?? matchedNote?.stroke_color ?? null,
            stroke_width: localEdit?.stroke_width ?? matchedNote?.stroke_width ?? null,
            user_id,
            type: 2,
            // Preserve local edit flags
            is_edit: localEdit?.is_edit,
            delete: localEdit?.delete,
          } as NoteAppRankingLocal;
        });
        return {
          ...prev,
          // Keep order from staffArr, not from merge
          staff: updatedStaffRanking,
        };
      });
    } else {
      setNotes((prev) => {
        const updatedRanking = rankArr.map(({ title, value }: rankArr) => {
          const sameRankItems = noteAppRanking?.filter(
            (x: any) =>
              x?.order_by_image_slider_ranking_id === null &&
              x?.program_rank_id === null &&
              x?.rank === value
          );

          const matchedNote =
            sameRankItems?.find((item) => item.shop_id === null) || sameRankItems?.[0];

          // Check if there's a local edit for this rank
          const localEdit = prev.ranking?.find(
            (r: NoteAppRankingLocal) => r.rank === value && r.is_edit
          );

          return {
            ...matchedNote,
            id: matchedNote?.id || null,
            title,
            value,
            note: localEdit?.note ?? matchedNote?.note ?? '',
            font: localEdit?.font ?? matchedNote?.font ?? null,
            font_size: localEdit?.font_size ?? matchedNote?.font_size ?? null,
            color: localEdit?.color ?? matchedNote?.color ?? null,
            stroke_color: localEdit?.stroke_color ?? matchedNote?.stroke_color ?? null,
            stroke_width: localEdit?.stroke_width ?? matchedNote?.stroke_width ?? null,
            rank: matchedNote?.rank ?? value,
            // Preserve local edit flags
            is_edit: localEdit?.is_edit,
            delete: localEdit?.delete,
          } as NoteAppRankingLocal;
        });
        return {
          ...prev,
          // Keep order from rankArr
          ranking: updatedRanking,
        };
      });
    }
  }, [
    selectRank,
    noteAppRanking.length,
    rankArr.length,
    staffArr.length,
    noteStaffAppRanking.length,
  ]);

  // const onEditNote = (e: any, item: any) => {
  //   const idx = noteRanking.findIndex((i: any, index: number) => i?.rank === item?.rank);
  //   if (!e.target.value) {
  //     noteRanking[idx] = {
  //       ...noteRanking[idx],
  //       note: e.target.value,
  //       delete: 1,
  //     };
  //   } else {
  //     noteRanking[idx] = {
  //       ...noteRanking[idx],
  //       note: e.target.value,
  //       delete: 0,
  //     };
  //   }
  //   noteRanking[idx] = { ...noteRanking[idx], note: e.target.value, is_edit: 1 };
  //   setNoteRanking([...noteRanking]);
  // };
  // const onEditNoteStaff = (e: any, item: any) => {
  //   const idx = noteStaffRanking.findIndex(
  //     (i: any, index: number) => i?.user_id === item?.cid_user
  //   );
  //   if (!e.target.value) {
  //     noteStaffRanking[idx] = {
  //       ...noteStaffRanking[idx],
  //       note: e.target.value,
  //       delete: 1,
  //     };
  //   } else {
  //     noteStaffRanking[idx] = {
  //       ...noteStaffRanking[idx],
  //       note: e.target.value,
  //       delete: 0,
  //     };
  //   }
  //   noteStaffRanking[idx] = { ...noteStaffRanking[idx], note: e.target.value, is_edit: 1 };
  //   setNoteStaffRanking([...noteStaffRanking]);
  // };

  const getNoteStaffAppRanking = useCallback(() => {
    if (selectShop?.id) {
      dispatch(
        rankingAction.getNoteStaffAppRanking({
          token,
          params: { shop_id: String(selectShop?.id), type: 2 },
        })
      );
    }
  }, [dispatch, token, selectShop?.id]);
  const getListNoteAppRanking = useCallback(() => {
    if (selectShop?.id) {
      dispatch(
        rankingAction.getNoteAppRanking({ token, params: { shop_id: String(selectShop?.id) } })
      );
    } else {
      dispatch(rankingAction.getNoteAppRanking({ token, params: { permission: 'master' } }));
    }
  }, [dispatch, token, selectShop?.id]);
  const onSave = useCallback(async () => {
    try {
      if (
        notes.staff?.filter((i: any) => i?.is_edit).length > 0 ||
        notes.ranking?.filter((i: any) => i?.is_edit).length > 0
      ) {
        if (selectRank === 'staff') {
          dispatch(
            rankingAction.updateNoteAppRanking({
              token,
              data: {
                shop_id: selectShop?.id,
                note_app_rankings: notes.staff?.filter((i: any) => i?.is_edit),
              },
            })
          );
        } else {
          if (isMaster) {
            dispatch(
              rankingAction.updateNoteAppRanking({
                token,
                data: {
                  shop_ids: listShopHasPermission,
                  note_app_rankings: notes.ranking?.filter((i: any) => i?.is_edit),
                },
              })
            );
          } else {
            dispatch(
              rankingAction.updateNoteAppRanking({
                token,
                data: {
                  shop_id: selectShop?.id,
                  note_app_rankings: notes.ranking?.filter((i: any) => i?.is_edit),
                },
              })
            );
          }
        }
        setTimeout(() => {
          successToast(t('UPDATE_SUCCESS'));
        }, 1000);
      }
    } catch (error) {
      console.log(error);
    }
  }, [notes, selectRank, dispatch, isMaster, listShopHasPermission, selectShop?.id, t, token]);
  useImperativeHandle(ref, () => ({
    onSave,
  }));
  const fetchHostForOrderList = useCallback(() => {
    if (selectShop?.id) {
      dispatch(
        rankingAction.fetchHostForOrderList({
          token,
          params: { shopId: selectShop?.id, per_page: 1000 },
        })
      );
    }
  }, [dispatch, selectShop?.id, token]);
  useEffect(() => {
    fetchHostForOrderList();
  }, [fetchHostForOrderList]);
  useEffect(() => {
    if (selectRank === 'staff') {
      getNoteStaffAppRanking();
    } else {
      getListNoteAppRanking();
    }
  }, [getListNoteAppRanking, selectRank, getNoteStaffAppRanking]);
  useEffect(() => {
    // lock scroll when modal open
    if (showModal) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  const handleOpenModal = useCallback((noteItem: NoteAppRankingLocal, index: number) => {
    setSelectedNote(noteItem);
    setEditingIndex(index);
    setShowModal(true);
  }, []);
  const handleSaveModal = useCallback(
    (updatedData: { note: string; styleConfig: NoteStyleConfig }) => {
      if (editingIndex === null || editingIndex < 0) return;

      // Check if note is empty → Mark as delete
      const isDeleting = !updatedData.note || !updatedData.note.trim();

      if (selectRank === 'staff') {
        setNotes((prev) => {
          const newStaff = [...prev.staff];
          // Ensure array has enough items
          if (editingIndex >= newStaff.length) {
            // If index is beyond array, add new item
            newStaff.push({
              ...selectedNote,
              note: updatedData.note,
              font: isDeleting ? null : updatedData.styleConfig.font,
              font_size: isDeleting ? null : updatedData.styleConfig.font_size,
              color: isDeleting ? null : updatedData.styleConfig.color,
              stroke_color: isDeleting ? null : updatedData.styleConfig.stroke_color,
              stroke_width: isDeleting ? null : updatedData.styleConfig.stroke_width,
              is_edit: 1,
              delete: isDeleting ? 1 : 0,
            } as NoteAppRankingLocal);
          } else {
            newStaff[editingIndex] = {
              ...newStaff[editingIndex],
              note: updatedData.note,
              font: isDeleting ? null : updatedData.styleConfig.font,
              font_size: isDeleting ? null : updatedData.styleConfig.font_size,
              color: isDeleting ? null : updatedData.styleConfig.color,
              stroke_color: isDeleting ? null : updatedData.styleConfig.stroke_color,
              stroke_width: isDeleting ? null : updatedData.styleConfig.stroke_width,
              is_edit: 1,
              delete: isDeleting ? 1 : 0,
            };
          }
          return { ...prev, staff: newStaff };
        });
      } else {
        setNotes((prev) => {
          const newRanking = [...prev.ranking];
          if (editingIndex >= newRanking.length) {
            newRanking.push({
              ...selectedNote,
              note: updatedData.note,
              font: isDeleting ? null : updatedData.styleConfig.font,
              font_size: isDeleting ? null : updatedData.styleConfig.font_size,
              color: isDeleting ? null : updatedData.styleConfig.color,
              stroke_color: isDeleting ? null : updatedData.styleConfig.stroke_color,
              stroke_width: isDeleting ? null : updatedData.styleConfig.stroke_width,
              type: 1, // Ensure type is set for ranking
              is_edit: 1,
              delete: isDeleting ? 1 : 0,
            } as NoteAppRankingLocal);
          } else {
            newRanking[editingIndex] = {
              ...newRanking[editingIndex],
              note: updatedData.note,
              font: isDeleting ? null : updatedData.styleConfig.font,
              font_size: isDeleting ? null : updatedData.styleConfig.font_size,
              color: isDeleting ? null : updatedData.styleConfig.color,
              stroke_color: isDeleting ? null : updatedData.styleConfig.stroke_color,
              stroke_width: isDeleting ? null : updatedData.styleConfig.stroke_width,
              type: 1, // Ensure type is set for ranking
              is_edit: 1,
              delete: isDeleting ? 1 : 0,
            };
          }
          return { ...prev, ranking: newRanking };
        });
      }

      setShowModal(false);
      setSelectedNote(null);
      setEditingIndex(null);
    },
    [selectRank, editingIndex, selectedNote]
  );
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedNote(null);
    setEditingIndex(null);
  }, []);

  return (
    <div
      style={{ height: window.innerHeight - (80 + 24 + 60) }}
      className="flex flex-col pt-[12px] px-[16px] flex-1 overflow-y-auto"
      aria-hidden={showModal}
    >
      <span className="text-[#252B37] text-[20px] notosans_bold">{t('MEMO')}</span>

      {/* display tab when is normal account */}
      {!isMaster && (
        <div className="flex items-center mt-[16px] border-b border-solid border-[#D5D7DA]">
          <button
            onClick={() => setSelectRank('staff')}
            className={`${
              selectRank === 'staff'
                ? 'text-[#666687] border-b-[3px] border-solid border-[#666687]'
                : 'text-[#939393] notosans_medium'
            }  text-[16px] w-[100px] h-[40px] justify-center flex items-center`}
          >
            スタッフ
          </button>
          <button
            onClick={() => setSelectRank('ranking')}
            className={`${
              selectRank === 'ranking'
                ? 'text-[#666687] notosans_bold border-b-[3px] border-solid border-[#666687]'
                : 'text-[#939393] notosans_medium'
            }  text-[16px] w-[100px] h-[40px] justify-center flex items-center`}
          >
            {t('RANKING_LIST')}
          </button>
        </div>
      )}

      {selectRank === 'staff' &&
        [...(hostForOrderList.data || [])]
          .sort((x: any, y: any) => (y?.orderby_staff || 0) - (x?.orderby_staff || 0))
          .map((x: any, y: number) => {
            const noteItem: NoteAppRankingLocal | undefined = notes.staff?.find(
              (note: any) => note?.user_id === x?.cid_user
            );

            // Ensure noteItem exists, fallback to default
            const safeNoteItem: NoteAppRankingLocal =
              noteItem ||
              ({
                user_id: x?.cid_user,
                note: '',
                font: null,
                font_size: null,
                color: null,
                stroke_color: null,
                stroke_width: null,
                type: 2,
              } as NoteAppRankingLocal);

            const styleConfig = getStyleConfig(safeNoteItem);

            // Find correct index in notes.staff array
            const staffIndex =
              notes.staff?.findIndex((note: any) => note?.user_id === x?.cid_user) ?? -1;

            return (
              <div
                key={y}
                className="flex flex-col md:flex-row items-start md:items-center py-[16px] border-b last:border-b-0 border-solid border-[#D5D7DA] gap-2 md:gap-0"
              >
                <div className="w-full md:w-[19.6%]">
                  <span className="text-[16px] text-[#252B37] notosans_medium">{x?.nick_name}</span>
                </div>
                <div className="w-full md:w-[43%]">
                  <Button
                    className="rounded-[8px] h-[44px] w-full font-notosans-400 text-left border  border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 flex items-center justify-start px-[11px]"
                    onClick={(e) => {
                      // If staffIndex is -1, we need to create new entry
                      const finalIndex = staffIndex >= 0 ? staffIndex : notes.staff?.length || 0;
                      handleOpenModal(safeNoteItem, finalIndex);
                    }}
                    style={{
                      fontFamily: styleConfig.font,
                      fontSize: `${Math.min(styleConfig.font_size, 16)}px`,
                      color: styleConfig.color,
                    }}
                  >
                    <span className="truncate w-full text-left text-[#252B37]">
                      {safeNoteItem?.note ?? ''}
                    </span>
                  </Button>
                </div>
              </div>
            );
          })}

      {selectRank === 'ranking' &&
        notes.ranking.map((x, index) => {
          const styleConfig = getStyleConfig(x);
          const checkIsMasterEdit = x.id
            ? isMaster
              ? !handleCheckMasterCreateNote(x)
              : handleCheckMasterCreateNote(x)
            : false;
          return (
            <div
              key={index}
              className={`flex flex-col md:flex-row items-start md:items-center py-[16px] ${
                index === notes.ranking.length - 1
                  ? ''
                  : 'border-b border-solid last:border-b-0 border-border-gray gap-2 md:gap-0'
              }`}
            >
              <div className="w-full md:w-[19.6%]">
                <span className=" text-[16px] text-[#252B37] notosans_medium">
                  {getRankTitle(x.rank!)}
                </span>
              </div>
              <div className="w-full md:w-[43%]">
                {/* <Input
                  className="rounded-[8px] h-[44px] w-full font-notosans-400"
                  value={x.note}
                  disabled={checkIsMasterEdit}
                  onChange={(e) => onEditNote(e, x)}
                /> */}
                <Button
                  className={`rounded-lg h-11 w-full font-notosans-400 text-left border border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 flex items-center justify-start px-[11px] 
                    ${checkIsMasterEdit ? 'cursor-not-allowed bg-gray-100 text-gray-500' : ''}`}
                  onClick={() => !checkIsMasterEdit && handleOpenModal(x, index)}
                  disabled={checkIsMasterEdit}
                  style={{
                    fontFamily: styleConfig.font,
                    fontSize: `${Math.min(styleConfig.font_size, 16)}px`,
                    color: checkIsMasterEdit ? '#999' : styleConfig.color,
                  }}
                >
                  <span
                    className={`truncate w-full text-left ${
                      checkIsMasterEdit ? 'text-gray-500' : 'text-[#252B37]'
                    }`}
                  >
                    {x.note || ''}
                  </span>
                </Button>
              </div>
              {checkIsMasterEdit && (
                <p className="ml-6 whitespace-nowrap text-gray_500 font-notosans-400">
                  {t('ADMINISTRATOR_UPLOADED')}
                </p>
              )}
            </div>
          );
        })}
      {showModal && selectedNote && (
        <>
          {/* overlay - captures all pointer events */}
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={(e) => e.stopPropagation()} // prevents clicks falling through
          />
          {/* modal wrapper - ensure higher z-index */}
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <ModalTextEditor
              onClose={handleCloseModal}
              initialNote={selectedNote?.note || ''}
              initialStyleConfig={getStyleConfig(selectedNote)}
              onSave={handleSaveModal}
              // previewImage={selectedNote.custom_preview_image}
            />
          </div>
        </>
      )}
    </div>
  );
});
export default Memo;
